import { useTranslation } from '@crew/modules/i18n'
import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FormValues, useGroupChatEditForm } from './useGroupChatEditForm'
import { GetProjectRequest } from '@crew/apis/project/models/getProject/request'
import { useGetProjectQuery } from '@crew/apis/project/projectApis'
import { skipToken } from '@reduxjs/toolkit/query'
import { generateImageAvatarUrl } from '@crew/utils/avatar'
import { EntityType } from '@crew/enums/domain'
import { convertToByte } from '@crew/utils/file'
import { useToast } from 'hooks/useToast'
import { Project } from '@crew/apis/project/models/getProject/response'
import { useShowApiErrorsWithForm } from 'hooks/useShowApiErrors'
import { useModal } from 'components/layouts/modal/useModal'
import { CrewConfirmDialog } from 'components/elements/crewConfirmDialog/crewConfirmDialog'
import { CrewButton } from 'components/elements/crewButton/crewButton'
import { CrewScrollView } from 'components/devextreme/crewScrollView'
import { CrewErrorSummary } from 'components/forms/crewErrorSummary'
import { useSystemPermissions } from '@crew/hooks'
import {
  CrewAvatar,
  CrewAvatarShapeType,
  CrewAvatarSize,
} from 'components/elements/crewAvatar/crewAvatar'
import { CrewTextBoxField } from 'components/forms/crewTextBoxField'

export type GroupChatEditFormProps = {
  projectId: string
  onSubmit: (projectId: string) => void
  onCancel: () => void
  onDeleted?: () => void
}

// アバターの最大ファイルサイズ
const MAX_FILE_SIZE = 1 // 1MB

export const GroupChatEditForm: FC<GroupChatEditFormProps> = memo((props) => {
  const { t } = useTranslation()
  const toast = useToast()

  const {
    handleSubmit,
    clearErrors,
    formState,
    reset,
    control,
    setError,
    setValue,
    getValues,
    validateRules,

    deleteGroupChat,
    updateGroupChat,
    isLoadingDeleteProjectForGroupChat,
    isLoadingUpdateProjectForGroupChat,

    uploadFile,
    convertBase64,
  } = useGroupChatEditForm()

  const [showApiErrors] = useShowApiErrorsWithForm(setError)

  const { hasSysProjectCreateDeletePermission } = useSystemPermissions()

  // グループチャット詳細の取得
  // 三項演算子になっていて少し見づらいが、内部のパラメータがundefinedを受け付けないため三項演算子を使用している
  const useGetGroupChatParam: GetProjectRequest | undefined = props.projectId
    ? {
        projectId: props.projectId,
      }
    : undefined

  const { data: getGroupChatResult } = useGetProjectQuery(
    useGetGroupChatParam ?? skipToken
  )
  const [project, setProject] = useState<Project>()

  // 確認ダイアログ
  const [isConfirmDialogOpen, openConfirmDialog, closeConfirmDialog] =
    useModal()
  const [confirmMessage, setConfirmMessage] = useState('')

  const imageRef = useRef<HTMLInputElement>(null)
  const [selectedImage, setSelectedImage] = useState<string | undefined>()
  const [displayName, setDisplayName] = useState<string>('G')
  const [initialAvatarStyle, setInitialAvatarStyle] = useState<string>('')

  // TODO: CREW-21003 実装後に正式に対応
  const imageUrl = generateImageAvatarUrl(
    EntityType.Project,
    getGroupChatResult?.project?.id
  )

  // 画像が変更されたときの処理
  const handleImageChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.files && event.target.files.length > 0) {
        try {
          const file = event.target.files[0]
          // validate file size
          if (file.size > convertToByte(MAX_FILE_SIZE, 'MB')) {
            toast.error(t('message.file.invalidMaxFileSize'))
            // make reselect file
            if (imageRef.current) {
              imageRef.current.value = ''
            }
            return
          }
          // 一時領域にファイルをupload
          const result = await uploadFile(file)
          setValue('avatarKey', result.key)
          const base64 = await convertBase64(file)
          setSelectedImage(base64 as string)

          // 初期スタイルが不要になるため空にする
          setInitialAvatarStyle('')
        } catch (err: any) {
          toast.error(t('message.file.uploadFailed'))
        }
      }
    },
    [convertBase64, setValue, t, toast, uploadFile]
  )

  // フォーム初期化処理関数
  const initializeForm = useCallback(() => {
    // 編集時
    if (getGroupChatResult?.project) {
      reset({
        subject: getGroupChatResult.project.subject,
      })

      setProject(getGroupChatResult.project ?? undefined)

      // 登録されている表示名・初期スタイル・バージョンのセット
      if (getGroupChatResult.project) {
        // 件名
        setDisplayName(getGroupChatResult.project.subject ?? 'p')
        // 既存データ表示時は初期スタイル不要
        setInitialAvatarStyle('')
      }
    }
  }, [getGroupChatResult?.project, reset])

  // 初期化処理をuseEffect化
  // TODO: 本当はuseEffectを外したいのだが、レンダリングが大量に走ってしまうためこのようにしている。以下タスクで調査・対応予定
  // https://break-tmc.atlassian.net/browse/CREW-6028
  useEffect(() => {
    // フォーム初期化を実行
    initializeForm()
  }, [initializeForm])

  const canSend = useMemo(
    () => Object.keys(formState.errors).length === 0 && !formState.isSubmitting,
    [formState]
  )

  // 画像アップロード処理
  const handleFileUpload = useCallback(() => {
    imageRef.current?.click()
  }, [])

  // 削除ボタンクリック時の処理
  const handleDeleteButtonClick = useCallback(() => {
    setConfirmMessage(t('message.general.confirmMessage.delete'))
    openConfirmDialog()
  }, [openConfirmDialog, t])

  // 削除確認ダイアログ OKボタン
  const handleDeletePermitButtonClick = useCallback(async () => {
    if (props.projectId && project) {
      try {
        await deleteGroupChat(props.projectId, project.version)

        toast.success(t('message.groupChat.groupChatDeleted'))
        props.onSubmit(props.projectId ?? '')
        props.onDeleted && props.onDeleted()
      } catch (err) {
        showApiErrors(err)
      }
    }

    closeConfirmDialog()
  }, [
    props,
    project,
    closeConfirmDialog,
    deleteGroupChat,
    toast,
    t,
    showApiErrors,
  ])

  // 登録ボタンクリック時の処理
  const handleSubmitButtonClick = useCallback(async () => {
    const onSubmit = async (data: FormValues) => {
      try {
        // 更新処理
        const result = await updateGroupChat(
          props?.projectId,
          project?.version ?? 0,
          data
        )
        props.onSubmit(result.project.id)
        toast.success(t('message.groupChat.groupChatUpdated'))
      } catch (err) {
        showApiErrors(err)
      }
    }
    handleSubmit(onSubmit)()
  }, [
    handleSubmit,
    updateGroupChat,
    props,
    project?.version,
    toast,
    t,
    showApiErrors,
  ])

  // ダイアログを閉じる処理
  const handleCancelButtonClick = useCallback(() => {
    reset()
    clearErrors()
    props.onCancel && props.onCancel()
  }, [clearErrors, props, reset])

  return (
    <>
      <form className="flex flex-col gap-y-5 h-full w-full">
        <CrewScrollView>
          <div className="space-y-5 ">
            <div className="flex flex-col gap-y-2.5">
              <div className="flex gap-x-2.5">
                <div className="text-center">
                  {/* TODO: CREW-21003 実装後に正式に対応 */}
                  <CrewAvatar
                    cacheValue={
                      getGroupChatResult?.project
                        ? (getGroupChatResult.project.id ?? '') +
                          (getGroupChatResult.project.version ?? '') // 編集時：プロジェクトID + バージョン
                        : (getValues('subject') ?? '') // 新規登録時：件名
                    }
                    // 画像が変更されている場合は変更後の画像、変更されていない場合は登録済みの画像を表示
                    imageURL={selectedImage ? undefined : imageUrl}
                    avatarImage={selectedImage}
                    displayName={displayName}
                    size={CrewAvatarSize.xl}
                    shape={CrewAvatarShapeType.Circle}
                    className={initialAvatarStyle}
                  />
                  <div className="text-center">
                    <input
                      ref={imageRef}
                      type="file"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                    <span
                      className="cursor-pointer leading-5"
                      onClick={handleFileUpload}
                    >
                      {t('label.change')}
                    </span>
                  </div>
                </div>
                <div className="flex-auto">
                  <CrewTextBoxField
                    id="subject"
                    name="subject"
                    control={control}
                    rules={validateRules.subject}
                    labelMode="hidden"
                    label={t('label.groupName')}
                    required={true}
                  />
                </div>
              </div>
            </div>
            <CrewErrorSummary formState={formState} />
          </div>
        </CrewScrollView>
        <div className="flex justify-between items-center">
          {hasSysProjectCreateDeletePermission && (
            <CrewButton
              id="group-chat-edit-form-delete-button"
              text={t('action.delete')}
              type="danger"
              onClick={handleDeleteButtonClick}
              disabled={isLoadingDeleteProjectForGroupChat}
            />
          )}

          <div className="ml-auto flex gap-x-5">
            <CrewButton
              id="group-chat-edit-form-submit-button"
              text={t('action.register')}
              type="primary"
              onClick={handleSubmitButtonClick}
              disabled={!canSend || isLoadingUpdateProjectForGroupChat}
            />
            <CrewButton
              id="group-chat-edit-form-cancel-button"
              text={t('action.cancel')}
              type="normal"
              stylingMode="outlined"
              onClick={handleCancelButtonClick}
            />
          </div>
        </div>
      </form>
      {/* 削除確認ダイアログ */}
      <CrewConfirmDialog
        isOpen={isConfirmDialogOpen}
        message={confirmMessage}
        onPermitButtonClick={handleDeletePermitButtonClick}
        onCancelButtonClick={closeConfirmDialog}
        permitButtonDisabled={isLoadingDeleteProjectForGroupChat}
      />
    </>
  )
})
